/**
 * Vendor plugins — what is installed, and what can be added.
 *
 * A *plugin* is a CAD integration such as Revit or AutoCAD. **The local agent
 * is deliberately not here.** It ships inside the
 * desktop app and the user neither installs nor manages it; a card for it would
 * invite an action that does not exist. Its state stays in the shell's footer,
 * beside the chat that depends on it.
 *
 * Two things this pane has to keep straight, because they look the same on
 * screen and mean opposite things:
 *
 *   - **"cannot detect" is not "detected none".** A browser, or a desktop older
 *     than bridge v4, cannot look at all; saying "no plugins installed" there
 *     would be a claim nothing checked (Corrections #23).
 *   - **A running session outranks the filesystem.** If a CAD host is open with our
 *     plugin loaded, that is the fact — whatever `detect` made of the disk.
 */
import { useMemo, useState, type ReactNode } from "react";
import { useLocale } from "../i18n/LocaleProvider";
import type { PluginsController } from "../hooks/usePlugins";
import { useAgentHealth, type Probe } from "../hooks/useAgentHealth";
import type { HostsView } from "../hooks/useHosts";
import { AlertIcon, InfoIcon } from "../components/skills/icons";
import { ManageButton, PluginManageDialog } from "../components/plugins/manage";
import {
  CATALOG,
  pluginView,
  updateTier,
  type CatalogEntry,
  type InstalledPluginView,
  type PluginId,
} from "../lib/plugins";
import type { CadInstance } from "../bimgo";
import type { HostReachability } from "../lib/agentHealth";
import type { AgentTarget } from "../lib/agentClient";

const EMPTY: CadInstance[] = [];

/**
 * One card per catalog entry, installed or not — the same card either way; only its status
 * and its action differ. Every entry is always listed (owner, 2026-10-22), so a fresh machine
 * sees what it can add without looking for it. Not installed: the description, a "Not installed" chip and ＋.
 * Installed: the years that carry the plugin, the build-status chip, open sessions and Manage.
 */
function PluginCard({
  entry,
  view,
  instances,
  reach,
  action,
}: {
  entry: CatalogEntry;
  view: InstalledPluginView;
  instances: readonly CadInstance[];
  /** Per session, keyed by `sessionId`: can anything actually talk to it? */
  reach: Record<string, Probe<HostReachability>>;
  /** The card's one action: ＋ before the first install, Manage after it. */
  action: ReactNode;
}) {
  const { t } = useLocale();
  const installed = view.status !== "not-installed";
  // Product versions carrying the plugin are listed oldest first, while action rows are newest first.
  const installedYears = view.versions
    .filter((v) => v.state === "ready")
    .map((v) => v.version)
    .sort((a, b) => a.localeCompare(b));
  // The pending changes on installed years decide the build word: a genuine newer build reads
  // "Available for upgrades"; when every pending change is a rollback (the pointer was moved
  // back off a bad build), it reads "Roll back available" instead of claiming an upgrade.
  const pendingReady = view.versions.filter((v) => v.state === "ready" && v.updateTo);
  const anyUpgrade = pendingReady.some(
    (v) => updateTier(v.installedPluginVersion ?? null, v.updateTo!) !== "downgrade",
  );
  // One build-status chip — the old status dot and the update pill merged into a single
  // colour-coded badge. It answers "is the on-disk build fine?": green installed, blue an
  // upgrade waits, amber a rollback or a half install. Running/sessions is operational, not
  // build state, so it stays in the session list below rather than in here.
  const buildStat = !entry.available
    ? { key: "plugins.catalog.comingSoon", tone: "idle" }
    : !installed
    ? { key: "plugins.status.notInstalled", tone: "idle" }
    : view.updateAvailable
    ? anyUpgrade
      ? { key: "plugins.status.updateAvailable", tone: "update" }
      : { key: "plugins.status.rollbackAvailable", tone: "rollback" }
    : view.status === "attention"
      ? { key: "plugins.status.attention", tone: "warn" }
      : { key: "plugins.status.installed", tone: "ok" };

  return (
    <li className="plugin-card">
      <div className="plugin-card-head">
        <span className={`plugin-glyph is-${view.id}`} aria-hidden="true">
          {view.id === "revit" ? "R" : "A"}
        </span>
        <div className="plugin-card-title">
          <span className="plugin-name">{view.name}</span>
          <span className="plugin-desc">{t(entry.descriptionKey)}</span>
        </div>
        {action}
      </div>

      {/* The years that carry the plugin — as chips, not the per-year list, which moved into
          Manage — beside the single colour-coded build-status chip at the bottom-right. */}
      <div className="plugin-meta">
        {installedYears.length > 0 ? (
          <div className="plugin-years">
            <span className="plugin-years-label">
              {t("plugins.card.installedLabel", {
                host: view.id === "revit" ? "Revit" : "AutoCAD",
              })}
            </span>
            {installedYears.map((year) => (
              <span key={year} className="year-chip">
                {year}
              </span>
            ))}
          </div>
        ) : (
          <span />
        )}
        <span className={`plugin-stat is-${buildStat.tone}`}>
          <span className="plugin-stat-dot" aria-hidden="true" />
          {t(buildStat.key)}
        </span>
      </div>

      {/* "Installed" is a fact about the disk. This is the other half the user
          came for: the session is open *and* something can reach it, which is
          what decides whether a chat turn gets CAD tools. */}
      {instances.length > 0 ? (
        <ul className="session-list">
          {instances.map((instance) => {
            const probe = reach[instance.sessionId];
            const connected = probe?.kind === "loaded" && probe.value.reachable;
            const tone =
              probe === undefined || probe.kind === "idle" || probe.kind === "loading"
                ? "idle"
                : connected
                  ? "ok"
                  : "bad";
            return (
              <li key={instance.sessionId} className="session-row">
                <span className={`state-dot is-${tone}`} aria-hidden="true" />
                <span className="session-title">
                  {instance.documentTitle || t("plugins.host.untitled")}
                </span>
                <span className="session-meta">
                  {instance.hostVersion ?? instance.revitVersion ?? "—"}
                </span>
                <span className="session-state">
                  {probe === undefined || probe.kind === "idle" || probe.kind === "loading"
                    ? t("plugins.session.checking")
                    : connected
                      ? t("plugins.session.connected")
                      : t("plugins.session.notResponding")}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </li>
  );
}

export default function PluginsPane({
  agent,
  hosts,
  plugins,
}: {
  /** Held by the shell; used here only to check sessions, never shown as a card. */
  agent: AgentTarget;
  hosts: HostsView;
  plugins: PluginsController;
}) {
  const { t } = useLocale();
  const [manageId, setManageId] = useState<PluginId | null>(null);

  const instances = hosts.kind === "loaded" ? hosts.instances : EMPTY;
  const snapshots = plugins.state.kind === "loaded" ? plugins.state.snapshots : null;

  const health = useAgentHealth(agent.ok, instances, 0);
  const views = useMemo(
    () => new Map(CATALOG.map((entry) => [entry.id, pluginView(snapshots, instances, entry.id)!])),
    [snapshots, instances],
  );

  return (
    <section data-pane="plugins">
      <div className="pane-head">
        <div className="pane-head-title">
          <h2 className="pane-head-h2">
            {t("plugins.title")}
            {/* Behind a glyph, as on Skills (owner, 2026-10-18) — hover or focus to read it. */}
            <span className="info-tip" tabIndex={0} role="note" aria-label={t("plugins.subtitle")}>
              <InfoIcon size={15} />
              <span className="info-tip-bubble" role="tooltip">{t("plugins.subtitle")}</span>
            </span>
          </h2>
        </div>
      </div>

      {plugins.state.kind === "unsupported" ? (
        // Not "no plugins": nothing looked. Saying otherwise would be a claim
        // this build cannot make.
        <div className="settings-card">
          <p className="settings-note">{t("plugins.unsupported")}</p>
          <p className="settings-note">{t("plugins.unsupportedHint")}</p>
        </div>
      ) : plugins.state.kind === "loading" ? (
        <div className="settings-card">
          <p className="settings-note">{t("plugins.checking")}</p>
        </div>
      ) : plugins.state.kind === "failed" ? (
        <div className="settings-card skills-empty plugins-error" role="alert">
          <span className="skills-empty-glyph is-warn"><AlertIcon size={22} /></span>
          <p className="skills-empty-title">{t("plugins.detectFailed")}</p>
          <p className="skills-empty-desc">{plugins.state.message}</p>
          <button type="button" className="settings-btn secondary" onClick={plugins.reload}>
            {t("plugins.retry")}
          </button>
        </div>
      ) : (
        <ul className="plugin-list">
          {CATALOG.map((entry) => {
            const view = views.get(entry.id)!;
            const installed = view.status !== "not-installed";
            return (
              <PluginCard
                key={entry.id}
                entry={entry}
                view={view}
                instances={instances.filter((instance) => instance.host_kind === entry.id)}
                reach={health.hosts}
                action={
                  installed ? (
                    <ManageButton
                      vendor={entry.id}
                      plugins={plugins}
                      instances={instances}
                      onClick={() => setManageId(entry.id)}
                    />
                  ) : (
                    // Opens the same Manage dialog, for a product with nothing on disk yet.
                    // Disabled for an announced-but-unshippable entry.
                    <button
                      type="button"
                      className="plugin-add"
                      disabled={!entry.available || plugins.busy}
                      aria-label={t("plugins.catalog.add", { name: entry.name })}
                      title={t("plugins.catalog.add", { name: entry.name })}
                      onClick={() => setManageId(entry.id)}
                    >
                      +
                    </button>
                  )
                }
              />
            );
          })}
        </ul>
      )}

      {/* One dialog for the pane, not one per card: ＋ opens it for a product that has
          no card yet, and it stays open while the first install makes that card appear. */}
      <PluginManageDialog
        open={manageId !== null}
        onClose={() => setManageId(null)}
        vendor={manageId ?? "revit"}
        plugins={plugins}
        instances={instances}
      />
    </section>
  );
}
